// src/components/StatusIndicator.tsx
import { Box, CircularProgress, Tooltip, Badge } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useOnlineStatus } from "../offline/useOnlineStatus";
import { useOfflineDownloadStore } from "../offline/offlineDownloadStore";
import { useNoteSyncStore } from "@/offline/noteSyncStore";

export default function StatusIndicator() {
  const online = useOnlineStatus();
  const navigate = useNavigate();

  const jobs = useOfflineDownloadStore((s) => s.jobs);
  const pendingCount = useNoteSyncStore((s) => s.pendingCount);
  const failedCount = useNoteSyncStore((s) => s.failedCount);
  const noteSyncing = useNoteSyncStore((s) => s.syncing);

  // Determine combined job state
  const hasDownloading = Object.values(jobs).some(
    (j) => j.status === "downloading"
  );

  const hasDeleting = Object.values(jobs).some(
    (j) => j.status === "deleting"
  );

  const totalQueued = pendingCount + failedCount;
  const hasOutstandingNotes = totalQueued > 0;
  const syncing = hasDownloading || hasDeleting || noteSyncing;
  const badgeColor = failedCount > 0 ? "error" : "warning";

  const baseDot = (() => {
    if (!online) {
      return {
        title: "Offline",
        node: (
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: "red",
            }}
          />
        ),
      };
    }

    if (syncing) {
      return {
        title: "Syncing data…",
        node: (
          <CircularProgress
            size={14}
            sx={{ color: "gold", animationDuration: "650ms" }}
          />
        ),
      };
    }

    if (hasOutstandingNotes) {
      return {
        title: failedCount
          ? `${totalQueued} notes need attention`
          : `${totalQueued} notes queued`,
        node: (
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: failedCount ? "orange" : "gold",
            }}
          />
        ),
      };
    }

    return {
      title: "Online · All synced",
      node: (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: "limegreen",
          }}
        />
      ),
    };
  })();

  const indicator = (
    <Tooltip title={baseDot.title}>
      <Badge
        overlap="circular"
        badgeContent={hasOutstandingNotes ? totalQueued : null}
        color={badgeColor as any}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {baseDot.node}
      </Badge>
    </Tooltip>
  );

  return (
    <Box
      onClick={() => {
        if (hasOutstandingNotes) {
          navigate("/sync");
        }
      }}
      sx={{ cursor: hasOutstandingNotes ? "pointer" : "default" }}
    >
      {indicator}
    </Box>
  );
}
